import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { CollectionGrid, PortfolioSummary } from "@/components/CollectionBinder";
import { AffiliateDisclosure } from "@/components/AffiliateDisclosure";
import { getCollectionByToken } from "@/lib/collection.functions";
import { COLLECTION_CAPACITY, COLLECTION_PAGES, totalsOf, type CollectionRow } from "@/lib/collection-shared";
import { ShareButton } from "@/components/ShareButton";

export const Route = createFileRoute("/collection-share/$token")({
  head: () => ({
    meta: [
      { title: "Shared Palworld Card Collection — Pal Stars" },
      {
        name: "description",
        content:
          "A shared Pal Stars Palworld card collection binder: every tracked card with buy price, sell price and profit. No account needed to view it.",
      },
      { property: "og:title", content: "Shared Palworld card collection" },
      { property: "og:description", content: "A Palworld card collection binder shared from Pal Stars — buy price, sell price and profit per card." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: SharedCollectionPage,
});

function SharedCollectionPage() {
  const { token } = Route.useParams();
  const fetchCollection = useServerFn(getCollectionByToken);
  const [state, setState] = useState<{ owner: string; cards: CollectionRow[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    fetchCollection({ data: { token } })
      .then((r) => setState(r as unknown as { owner: string; cards: CollectionRow[] }))
      .catch(() => setError("This share link is no longer active. Ask the collector for a fresh link."));
  }, [token, fetchCollection]);

  const map = useMemo(() => {
    const m = new Map<number, CollectionRow>();
    (state?.cards ?? []).forEach((c) => {
      if (c.slot !== null) m.set(c.slot, c);
    });
    return m;
  }, [state]);
  const totals = useMemo(() => totalsOf(state?.cards ?? []), [state]);

  if (error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Share link closed</h1>
        <p className="mt-3 text-sm text-muted-foreground">{error}</p>
        <Link to="/cards" className="mt-5 inline-block rounded-md bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground">
          Browse the Palworld card database
        </Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8 space-y-6">
      <header>
        <p className="text-xs uppercase tracking-widest text-primary">Shared collection</p>
        <div className="flex flex-wrap items-start justify-between gap-3">
        <h1 className="mt-2 text-3xl font-bold">{state?.owner ?? "Trainer"}'s Palworld card collection</h1>
          <ShareButton title={`${state?.owner ?? "Trainer"}'s Palworld card collection — Pal Stars`} text="A shared Palworld card collection on Pal Stars." />
        </div>
        <p className="aeo-answer mt-2 text-sm text-muted-foreground">
          {map.size} of {COLLECTION_CAPACITY} pockets filled. Each pocket shows the card, what the collector paid, what
          they are selling it for and the profit — page {page + 1} of {COLLECTION_PAGES}. You do not need an account to
          view this collection.
        </p>
      </header>

      <AffiliateDisclosure />

      <PortfolioSummary totals={totals} />

      <div className="glass rounded-2xl p-4 sm:p-6">
        <CollectionGrid page={page} cards={map} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setPage((p) => Math.max(0, p - 1))}
          disabled={page === 0}
          className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
        >
          ← Previous page
        </button>
        <button
          type="button"
          onClick={() => setPage((p) => Math.min(COLLECTION_PAGES - 1, p + 1))}
          disabled={page >= COLLECTION_PAGES - 1}
          className="rounded-md border px-3 py-1.5 text-sm font-semibold hover:bg-card disabled:opacity-40"
        >
          Next page →
        </button>
        <Link to="/collection" className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:brightness-110">
          Start your own collection binder
        </Link>
      </div>
    </div>
  );
}
